<template>
  <nav
    class="stepper-breadcrumb"
    :class="{
      'stepper-breadcrumb--expand': expand,
      'stepper-breadcrumb--divider': divider,
    }"
    :aria-label="ariaLabel"
    ref="scroller"
  >
    <ol ref="stepList">
      <li
        v-for="(item, index) in items"
        :key="index"
        :class="{
          'is-active': index === activeIndex,
          'is-completed': index < activeIndex,
        }"
        :aria-current="index === activeIndex ? 'step' : null"
      >
        <a
          href="#"
          class="stepper-breadcrumb__link"
          @click.prevent="$emit('change', index)"
        >
          <i v-if="item.icon" :class="item.icon" class="stepper-breadcrumb__icon" aria-hidden="true"></i>
          <span class="stepper-breadcrumb__label">{{ item.label }}</span>
        </a>
      </li>
    </ol>
  </nav>
</template>

<script>
export default {
  name: "StepperBreadcrumb",
  model: {
    prop: "activeIndex",
    event: "change",
  },
  props: {
    /**
     * Step items array. Each item: { label: String, icon?: String }
     * - label: Display text (required)
     * - icon: CSS icon class, e.g. "uil-cog" (optional)
     */
    items: {
      type: Array,
      required: true,
    },
    /** Active step index (supports v-model) */
    activeIndex: {
      type: Number,
      default: 0,
    },
    ariaLabel: {
      type: String,
      default: "Steps",
    },
    /**
     * Adimlar kapsayicinin tam genisligini esit paylasir (UIkit'teki
     * `uk-child-width-expand` karsiligi). Varsayilan kapali: icerige gore
     * genislik + tasarsa yatay kaydirma.
     *
     * Mobilde ETKISIZ — dar ekranda adimlari sikistirmak etiketleri
     * okunmaz hale getiriyor, orada kaydirma korunuyor.
     */
    expand: {
      type: Boolean,
      default: false,
    },
    /**
     * Adimlarin altinda tum genisligi kaplayan ayirici cizgi.
     * Varsayilan kapali.
     */
    divider: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    activeIndex() {
      this.$nextTick(() => this.scrollToActive());
    },
  },
  mounted() {
    this.$nextTick(() => this.scrollToActive());
  },
  methods: {
    scrollToActive() {
      const scroller = this.$refs.scroller;
      const list = this.$refs.stepList;
      if (!scroller || !list) return;
      const activeItem = list.children[this.activeIndex];
      if (!activeItem) return;

      const scrollerRect = scroller.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      const targetScroll =
        scroller.scrollLeft +
        (itemRect.left - scrollerRect.left) -
        scrollerRect.width / 2 +
        itemRect.width / 2;

      scroller.scrollTo({ left: Math.max(0, targetScroll), behavior: "smooth" });
    },
  },
};
</script>

<style scoped>
.stepper-breadcrumb {
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.stepper-breadcrumb::-webkit-scrollbar {
  display: none;
}

.stepper-breadcrumb ol {
  display: flex;
  align-items: stretch;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  width: max-content;
  min-width: 100%;
}

.stepper-breadcrumb ol li {
  position: relative;
  flex: 0 0 auto;
}

/* divider: cizgi <nav>'da, <ol>'de DEGIL. ol `width: max-content` tasidigi
   icin cizgi ona verilse yatay kaydirmada icerikle birlikte kayar ve
   kapsayicinin tamamini kaplamaz. */
.stepper-breadcrumb--divider {
  border-bottom: 1px solid #e2e8f0;
}

/* expand: adimlar tam genisligi esit paylasir (yalnizca web) */
@media (min-width: 960px) {
  .stepper-breadcrumb--expand ol {
    width: 100%;
  }

  .stepper-breadcrumb--expand ol li {
    flex: 1 1 0;
    min-width: 0;
  }
}

/* Link */
.stepper-breadcrumb__link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px 20px;
  border-bottom: 2px solid transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  text-decoration: none;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.15s, border-color 0.15s;
}

.stepper-breadcrumb__icon {
  font-size: 17px;
  line-height: 1;
}

.stepper-breadcrumb__label {
  line-height: 1;
}

/* Completed */
.stepper-breadcrumb ol li.is-completed .stepper-breadcrumb__link {
  color: var(--primaryColor, #3e416d);
}

/* Active */
.stepper-breadcrumb ol li.is-active .stepper-breadcrumb__link {
  color: var(--primaryColor, #3e416d);
  font-weight: 600;
  border-bottom-color: var(--primaryColor, #3e416d);
}

/* ==================== Mobile ==================== */
@media (max-width: 959px) {
  .stepper-breadcrumb__link {
    min-height: 48px;
    padding: 14px 14px;
    font-size: 14px;
  }

  .stepper-breadcrumb__icon {
    font-size: 18px;
  }
}

/* ==================== Hover ==================== */
@media (hover: hover) and (pointer: fine) {
  .stepper-breadcrumb__link:hover {
    color: var(--primaryColor, #3e416d);
    cursor: pointer;
  }
}

/* ==================== Focus ==================== */
.stepper-breadcrumb__link:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.28);
  outline-offset: -3px;
}

/* ==================== Reduced Motion ==================== */
@media (prefers-reduced-motion: reduce) {
  .stepper-breadcrumb__link {
    transition: none;
  }
}
</style>
